<template>
  <!-- Vue Native Modal -->
  <div v-if="is_modal_visible" class="modal-overlay" @click="close_modal">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content" @click.stop>
    <div class="modal-header cute-header shadow-lg">
      <p class="modal-title text-white text-xxs">Select A Verification Service To Run</p>
      <button
        type="button"
        class="modal-btn-close btn-close"
        @click="close_modal"
        aria-label="Close"
      ></button>
    </div>
    <div class="modal-body">
      <div class="container p-4">
          <div
            class="col-12 mb-3"
            v-for="(service, index) in verification_services"
            :key="index"
          >
            <div
              class="d-flex justify-content-between align-items-center border table-border-color rounded-lg p-3 shadow-sm"
              :class="{
                'opacity-5': disabled_service(service),
                'pe-none': false
              }"
              :aria-disabled="disabled_service(service) ? 'true' : 'false'"
            >
              <div class="d-flex align-items-start gap-3">
                <!-- Icon -->
                <i class="bi bi-file-earmark-text fs-4 flex-shrink-0 mt-1"></i>
                <!-- Content -->
                <div class="flex-grow-1">
                  <div class="d-flex align-items-center gap-2 mb-1">
                    <p class="mb-0 text-xs fw-bold">{{ service.name }}</p>
                  </div>
                  <small class="text-muted text-xs">{{ service.description }}</small>
                  <!-- Loading indicator for individual service -->
                  <div v-if="service.loading" class="mt-1">
                    <small class="text-primary text-xs">
                      <i class="fas fa-spinner fa-spin me-1"></i>
                      Verifying...
                    </small>
                  </div>
                  <!-- Status indicator -->
                  <div v-if="service.status" class="mt-1">
                    <small :class="`text-xs ${service.status === 'success' ? 'text-success' : 'text-danger'}`">
                      {{ service.status === 'success' ? '✓ Verified' : '✗ Failed' }}
                    </small>
                  </div>
                </div>
              </div>

              <!-- Checkbox -->
              <label class="text-xs mb-0 pe-auto">
                <input
                  type="checkbox"
                  v-model="service.selected"
                  class="form-check-input"
                  :disabled="disabled_service(service) || service.loading"
                  @change="on_service_selection_change(service)"
                />
              </label>
            </div>
          </div>
        <div class="d-flex justify-content-end gap-2 mt-2 text-xxs">
          <button
            class="d-none btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark d-inline-flex align-items-center"
            @click="start_verification"
            :disabled="is_verifying || !has_selected_services"
          >
            <span
              v-if="is_verifying"
              class="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
            <span>{{ is_verifying ? 'Starting…' : 'Start verification' }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { GET_PROJECTS_ASSIGNED_TO_SERVICE, get_headers } from "@/utils/constants/constants.js";
import { kycVerify } from "@/services/verification/integrated_verification_apis/kyc_verification";
import { amlVerify } from "@/services/verification/integrated_verification_apis/aml_verification.js";
import { identityverificationVerify } from "@/services/verification/integrated_verification_apis/idv_verification.js";
import { cipcVerify } from "@/services/verification/integrated_verification_apis/cipc_verification.js";
import { creditreportVerify } from "@/services/verification/integrated_verification_apis/credit_report_verification.js";
import alert_mixin from '@/utils/mixins/alert_mixin';

export default {
  name: "VerificationServicesModal",
  mixins: [alert_mixin],
  props: {
    isVerifying: {
      type: Boolean,
      default: false
    },
    entityData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      verification_services: [],
      loading: false,
      error: null,
      is_modal_visible: false,
      project_id: sessionStorage.getItem('project_id'),
    };
  },
  computed: {
    is_verifying() {
      return this.isVerifying;
    },
    has_selected_services() {
      return this.verification_services.some((service) => service.selected && !this.disabled_service(service));
    }
  },
  mounted() {
    this.fetch_verification_services();
  },
  methods: {
    // Normalise API logical status -> 'success' | 'error' | other
    normalize_api_status(status) {
      if (!status) return "error";
      const verification_status = status.toString().toLowerCase();

      if (
        ["success", "pass", "passed", "verified", "completed", "ok"].includes(verification_status)
      ) {
        return "success";
      }
      if (["fail", "failed", "error"].includes(verification_status)) {
        return "error";
      }
      return verification_status;
    },

    async fetch_verification_services() {
      this.loading = true;
      this.error = null;

      try {
        const response = await axios.get(GET_PROJECTS_ASSIGNED_TO_SERVICE, {
          headers: get_headers(),
          params: { project_id: this.project_id },
        });

        const parsed_response = typeof response.data === 'string'
          ? JSON.parse(response.data)
          : response.data;

        if (parsed_response.status === 'success' && parsed_response.data) {
          this.verification_services = parsed_response.data.map((service) => ({
            id: service.id,
            name: service.name,
            description: service.description,
            price: service.price,
            required_fields: service.required_fields,
            date_created: service.date_created,
            is_active: service.is_active,
            selected: false,
            loading: false,
            status: null, // 'success' | 'error' | null
          }));
        } else {
          this.$emit('error', parsed_response.message || 'Failed to fetch services');
        }
      } catch (error) {
        this.error = 'Failed to load verification services. Please try again.';
        this.$emit('error', this.error);
      } finally {
        this.loading = false;
      }
    },

    disabled_service(service) {
      // Only disable specific services that shouldn't be selectable
      const disabled_services = []; // Add service names you want to disable
      return disabled_services.includes(service.name?.toUpperCase());
    },

    get_selected_services() {
      return this.verification_services.filter(
        (service) => service.selected && !this.disabled_service(service)
      );
    },

    // Save selected service IDs to sessionStorage
    save_selected_services_to_session_storage() {
      const selected_services = this.get_selected_services();
      const selected_service_ids = selected_services.map(
        (service) => service.id
      );
      sessionStorage.setItem('selected_verification_services', JSON.stringify(selected_service_ids));
    },

    // Get selected service IDs from sessionStorage
    get_selected_service_ids_from_session_storage() {
      const stored_services = sessionStorage.getItem('selected_verification_services');
      return stored_services ? JSON.parse(stored_services) : [];
    },

    // This method runs immediately when a service checkbox is changed
    async on_service_selection_change(service) {
      const selected = service.selected;

      if (selected) {
        this.save_service_id_to_session_storage(service.id);
        this.start_service_verification(service);
      } else {
        this.remove_service_id_from_session_storage(service.id);
        service.status = null;
      }
    },

    // Save individual service_id to sessionStorage
    save_service_id_to_session_storage(serviceId) {
      const current_services = this.get_selected_service_ids_from_session_storage();
      if (!current_services.includes(serviceId)) {
        current_services.push(serviceId);
        sessionStorage.setItem('selected_verification_services', JSON.stringify(current_services));
      }
    },

    // Remove service_id from sessionStorage
    remove_service_id_from_session_storage(serviceId) {
      const current_services = this.get_selected_service_ids_from_session_storage();
      const updated_services = current_services.filter((id) => id !== serviceId);
      sessionStorage.setItem('selected_verification_services', JSON.stringify(updated_services));
    },

    // Route to the appropriate API based on service name
    async call_verification_api(service, payload) {
      const name = (service.name || "").toLowerCase();

      if (name.includes("aml")) {
        return amlVerify(payload);
      } else if (name.includes("credit report")) {
        return creditreportVerify(payload);
      } else if (name.includes("cipc")) {
        return cipcVerify(payload);
      } else if (name.includes("id verification") || name.includes("idv")) {
        return identityverificationVerify(payload);
      } else if (name.includes("kyc")) {
        return kycVerify(payload);
      }

      // fallback
      return kycVerify(payload);
    },

    // Start verification for a single service
    async start_service_verification(service) {
      const application_form_id = sessionStorage.getItem("application_form_id");
      const service_id = service.id;

      if (!application_form_id) {
        this.$nextTick(() => {
          service.selected = false;
        });
        return;
      }

      service.loading = true;
      service.status = null;

      try {
        const payload = {
          service_id,
          application_form_id: application_form_id,
        };

        sessionStorage.setItem("current_service_id", service_id.toString());

        const result = await this.call_verification_api(service, payload);
        const normalized = this.normalize_api_status(result?.status);

        service.loading = false;
        service.status = normalized;

        this.save_verification_result_to_session_storage(
          service,
          result,
          normalized
        );

        this.$emit("service-verified", {
          service,
          result,
        });
      } catch (error) {
        service.loading = false;
        service.status = 'error';
        this.$emit("service-error", {
          service,
          error,
        });
        const error_message = error?.response?.data?.detail || error?.response?.data?.message || error.message;
        const clean_message = this.format_error_message(error_message);
        this.show_error(clean_message);
      }
    },

    // Save verification result to sessionStorage
    save_verification_result_to_session_storage(service, result, status) {
      const verification_results = JSON.parse(sessionStorage.getItem('verification_results') || '{}');
      verification_results[service.id] = {
        service_id: service.id,
        service_name: service.name,
        result,
        timestamp: new Date().toISOString(),
        status,
      };
      sessionStorage.setItem('verification_results', JSON.stringify(verification_results));
    },

    reset_selections() {
      this.verification_services.forEach((service) => {
        service.selected = false;
        service.loading = false;
        service.status = null;
      });
      sessionStorage.removeItem('selected_verification_services');
      sessionStorage.removeItem('current_service_id');
    },

    open_modal() {
      this.is_modal_visible = true;
      this.reset_selections();
    },

    close_modal() {
      this.is_modal_visible = false;
    },

    start_verification() {
      this.save_selected_services_to_session_storage();
      this.$emit('start-verification');
    },

    format_error_message(message) {
      if (!message) return message;

      message = message.replace(/[[\]'"]+/g, "");
      message = message.replace(/_/g, " ");
      message = message.replace(/\b([a-z])/g, (m) => m.toUpperCase());
      return message;
    }

  },
  emits: ['start-verification', 'error', 'service-verified', 'service-error']
};
</script>

<style>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
}


@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive design */
@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    margin: 1rem;
  }
}</style>